import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { Context } from "@scenes/workloads/context";
import { Views } from "@scenes/workloads/api/views";
import { reCalculateDateRange } from "@scenes/workloads/utils";
import { useElementContext } from "../context";
import { getDate, getPreciseDate } from "../utils/getDate";
import { isMilestone } from "../utils";
import { Props } from "./types";
import {
  calculateLeftMargin,
  calculateStartAndEnd,
  calculateWidth,
} from "./utils";
import styles from "./styles.scss";

const DAY = 1000 * 60 * 60 * 24;
const HEIGHT_RATIO = 8 / 10;

function Root({ element, minDate, children }: Props) {
  const userTimezone = DateTime.local().zoneName;
  const { state } = useElementContext();
  const { resize, drag } = state;
  const {
    cell,
    areDerivationsToggled,
    hoveredElement,
    hoverElement,
    leaveElement,
    currentView,
  } = React.useContext(Context);
  const isDaily = currentView === Views.DAILY;
  const completionDate = getDate(element.completionDate, userTimezone);
  const recalculated = reCalculateDateRange(
    element,
    DateTime.fromISO(state.minDate, { zone: "utc" }),
    DateTime.fromISO(state.maxDate, { zone: "utc" }),
  );
  // Day views snap both edges to user-TZ day starts; the daily view keeps
  // exact time-of-day. A milestone's derived startDate is a day-start
  // artifact (see reCalculateLeafDateRange), so at hour precision it is
  // anchored on its own instant instead.
  const preciseStart = element.startDate
    ? getPreciseDate(element.startDate, userTimezone)
    : recalculated.startDate.setZone(userTimezone);
  const preciseEnd = element.endDate
    ? getPreciseDate(element.endDate, userTimezone)
    : recalculated.endDate.setZone(userTimezone);
  const { startDate, endDate } = calculateStartAndEnd(
    element,
    completionDate,
    isDaily
      ? {
          startDate: isMilestone(element) ? preciseEnd : preciseStart,
          endDate: preciseEnd,
        }
      : {
          startDate: element.startDate
            ? getDate(element.startDate, userTimezone)
            : recalculated.startDate.setZone(userTimezone).startOf("day"),
          endDate: element.endDate
            ? getDate(element.endDate, userTimezone)
            : recalculated.endDate.setZone(userTimezone).startOf("day"),
        },
    areDerivationsToggled,
  );
  // Date-only elements (both edges at user-TZ midnight — what day-view
  // drags and resizes write) run through their whole last day; without the
  // extra day their midnight end would render them one day short in the
  // daily view. Timed elements keep their exact end instant. dayDiff stays
  // the raw duration — Draggable rebuilds dates from it, so the display
  // extension must not leak into writes.
  const isDateOnly =
    startDate.toMillis() === startDate.startOf("day").toMillis() &&
    endDate.toMillis() === endDate.startOf("day").toMillis();
  const displayEndDate =
    isDaily && isDateOnly ? endDate.plus({ days: 1 }) : endDate;
  const offset = (startDate.toMillis() - minDate.toMillis() + DAY) / DAY - 1;
  const dayDiff = (endDate.toMillis() - startDate.toMillis()) / DAY;
  const displayDayDiff =
    (displayEndDate.toMillis() - startDate.toMillis()) / DAY;
  const height = cell.height * HEIGHT_RATIO;
  const width = calculateWidth(
    cell,
    offset,
    displayDayDiff,
    resize,
    element,
    isDaily,
  );

  return (
    <div
      id={`projectElementRoot:${element.uuid}`}
      className={classNames(styles.root, {
        [styles.dragged]:
          drag.isDragging || resize.left.isResizing || resize.right.isResizing,
      })}
      style={{
        width,
        height: cell.height,
        marginLeft: calculateLeftMargin(cell, drag, resize, offset, isDaily),
      }}
      onMouseEnter={() => hoverElement(element)}
      onMouseLeave={() => leaveElement(element)}
    >
      {children({
        width,
        height,
        offset,
        dayDiff,
        isHovered: !!hoveredElement && hoveredElement.uuid === element.uuid,
        isDragging: drag.isDragging,
      })}
    </div>
  );
}

export { Root };
